import { useState, useEffect } from 'react';
import type { LiveConversation } from './inboxTypes';
import { useI18n } from './i18n';

export interface WindowState {
  minsLeft: number | null;
  expired: boolean;
  noInbound: boolean;
  label: string;
  warn: boolean;
  isLocked: boolean;
}

/**
 * Pure calculation helper for the 24-hour customer care window state.
 */
export function computeWindowState(
  conv: LiveConversation | null | undefined,
  now = Date.now(),
  t: (key: string) => string = (k) => k
): WindowState {
  if (!conv) {
    return {
      minsLeft: null,
      expired: true,
      noInbound: true,
      label: t('inbox.window.none') || 'No inbound',
      warn: true,
      isLocked: true,
    };
  }

  if (conv.windowMinsLeft === null) {
    return {
      minsLeft: null,
      expired: true,
      noInbound: true,
      label: t('inbox.window.none') || 'No inbound',
      warn: true,
      isLocked: true,
    };
  }

  // Determine target expiration millis
  const expiresAt =
    (conv as any).windowExpiresAt ??
    (conv.lastInboundAt ? conv.lastInboundAt + 24 * 3600_000 : null);

  let minsLeft = conv.windowMinsLeft;
  if (expiresAt) {
    const remainingMs = expiresAt - now;
    minsLeft = Math.floor(remainingMs / 60000);
  }

  const expired = minsLeft <= 0;
  const isLocked = expired;

  if (expired) {
    return {
      minsLeft: 0,
      expired: true,
      noInbound: false,
      label: t('inbox.window.expired') || 'Window closed',
      warn: true,
      isLocked,
    };
  }

  const h = Math.floor(minsLeft / 60);
  const m = minsLeft % 60;
  const label = `${h}h ${m}m ${t('inbox.window.left') || 'left'}`;
  const warn = minsLeft < 120; // Warn if under 2 hours

  return {
    minsLeft,
    expired: false,
    noInbound: false,
    label,
    warn,
    isLocked,
  };
}

/**
 * Live 24-hour customer care window timer hook (PRD §1.7, WP-26).
 * Automatically updates every 15 seconds without a page reload.
 * As the window closes, transitions to expired and locks the composer in real-time.
 */
export function use24hWindow(conv: LiveConversation | null | undefined): WindowState {
  const { t } = useI18n();

  const [state, setState] = useState<WindowState>(() => computeWindowState(conv, Date.now(), t));

  useEffect(() => {
    setState(computeWindowState(conv, Date.now(), t));

    if (!conv || conv.windowMinsLeft === null) return;

    const interval = setInterval(() => {
      setState(computeWindowState(conv, Date.now(), t));
    }, 15000); // Check every 15s

    return () => clearInterval(interval);
  }, [conv?.id, conv?.windowMinsLeft, (conv as any)?.windowExpiresAt, conv?.lastInboundAt, t]);

  return state;
}
